import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  createRuntimeCronJob,
  deleteRuntimeCronJob,
  formatApiError,
  getRuntimeCronJobRuns,
  getRuntimeCronJobs,
  getRuntimeCronJobsOverview,
  pauseRuntimeCronJob,
  resumeRuntimeCronJob,
  runNowRuntimeCronJob,
  updateRuntimeCronJob,
} from '../lib/api';

const initialForm = {
  name: '',
  instructions: '',
  target_agent: 'orion',
  schedule_kind: 'interval',
  schedule_value: '15:minutes',
  timezone: 'America/New_York',
  model_override: 'basic',
  delivery_mode: 'silent',
  delivery_target: '',
  max_retries: 0,
  retry_backoff_seconds: 300,
  announce_on_failure: false,
  enabled: true,
};

export default function CronJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [overview, setOverview] = useState({ total_jobs: 0, enabled_jobs: 0, paused_jobs: 0, failed_jobs: 0, overdue_jobs: 0 });
  const [selectedJobId, setSelectedJobId] = useState('');
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState('');

  const selectedJob = useMemo(() => jobs.find((job) => job.id === selectedJobId) || null, [jobs, selectedJobId]);

  const loadJobs = async () => {
    const [{ data: jobsData }, { data: overviewData }] = await Promise.all([getRuntimeCronJobs(), getRuntimeCronJobsOverview()]);
    setJobs(jobsData || []);
    setOverview(overviewData || { total_jobs: 0, enabled_jobs: 0, paused_jobs: 0, failed_jobs: 0, overdue_jobs: 0 });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadJobs();
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedJobId) {
      setRuns([]);
      return;
    }
    (async () => {
      try {
        const { data } = await getRuntimeCronJobRuns(selectedJobId);
        setRuns(data || []);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [selectedJobId]);

  const loadRuns = async (jobId) => {
    const { data } = await getRuntimeCronJobRuns(jobId);
    setRuns(data || []);
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId('');
  };

  const startEdit = (job) => {
    setEditingId(job.id);
    setSelectedJobId(job.id);
    setForm({
      name: job.name || '',
      instructions: job.instructions || '',
      target_agent: job.target_agent || 'orion',
      schedule_kind: job.schedule_kind || 'interval',
      schedule_value: job.schedule_value || '15:minutes',
      timezone: job.timezone || 'America/New_York',
      model_override: job.model_override || 'basic',
      delivery_mode: job.delivery_mode || 'silent',
      delivery_target: job.delivery_target || '',
      max_retries: Number(job.max_retries || 0),
      retry_backoff_seconds: Number(job.retry_backoff_seconds || 300),
      announce_on_failure: !!job.announce_on_failure,
      enabled: !!job.enabled,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (editingId) {
        await updateRuntimeCronJob(editingId, form);
      } else {
        await createRuntimeCronJob(form);
      }
      resetForm();
      await loadJobs();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePauseResume = async (job) => {
    try {
      setError('');
      if (job.enabled) await pauseRuntimeCronJob(job.id);
      else await resumeRuntimeCronJob(job.id);
      await loadJobs();
      if (selectedJobId === job.id) await loadRuns(job.id);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleRunNow = async (job) => {
    try {
      setError('');
      await runNowRuntimeCronJob(job.id);
      await loadJobs();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDelete = async (job) => {
    try {
      setError('');
      await deleteRuntimeCronJob(job.id);
      if (selectedJobId === job.id) {
        setSelectedJobId('');
        setRuns([]);
      }
      if (editingId === job.id) resetForm();
      await loadJobs();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const examples = [
    'Every 15 minutes, summarize new inbound leads silently.',
    'At 9am every weekday, have Orion send me a planning summary.',
    'Every 6 hours, check failed integrations and only announce on failure.',
  ];

  return (
    <Layout>
      <div className="p-8 space-y-6" data-testid="cron-jobs-page">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Cron Jobs</h1>
          <p className="text-muted-foreground mt-1">Recurring background agent activity with low-cost defaults and optional delivery.</p>
        </div>
        {error ? <div className="apollo-card p-4 text-sm text-red-400">{error}</div> : null}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="apollo-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total jobs</div><div className="text-2xl font-semibold mt-1">{overview.total_jobs || 0}</div></CardContent></Card>
          <Card className="apollo-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Enabled</div><div className="text-2xl font-semibold mt-1">{overview.enabled_jobs || 0}</div></CardContent></Card>
          <Card className="apollo-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Paused</div><div className="text-2xl font-semibold mt-1">{overview.paused_jobs || 0}</div></CardContent></Card>
          <Card className="apollo-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Failed</div><div className="text-2xl font-semibold mt-1">{overview.failed_jobs || 0}</div></CardContent></Card>
          <Card className="apollo-card"><CardContent className="p-4"><div className="text-xs text-muted-foreground">Overdue</div><div className="text-2xl font-semibold mt-1">{overview.overdue_jobs || 0}</div></CardContent></Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="apollo-card lg:col-span-1">
            <CardHeader><CardTitle>{editingId ? 'Edit cron job' : 'New cron job'}</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="rounded-xl border border-border/70 p-3 bg-black/10">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Examples</div>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {examples.map((example) => <li key={example}>• {example}</li>)}
                </ul>
                <div className="mt-3 text-xs text-muted-foreground">Agent-side intake format also works with lines like <span className="text-foreground">name:</span>, <span className="text-foreground">schedule:</span>, <span className="text-foreground">agent:</span>, <span className="text-foreground">delivery_target:</span>.</div>
              </div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Instructions</Label><textarea className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></div>
              <div><Label>Target agent</Label><Input value={form.target_agent} onChange={(e) => setForm({ ...form, target_agent: e.target.value })} /></div>
              <div><Label>Schedule kind</Label><select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.schedule_kind} onChange={(e) => setForm({ ...form, schedule_kind: e.target.value })}><option value="interval">interval</option><option value="cron">cron</option></select></div>
              <div><Label>Schedule value</Label><Input value={form.schedule_value} onChange={(e) => setForm({ ...form, schedule_value: e.target.value })} placeholder="15:minutes or 0 9 * * *" /></div>
              <div><Label>Timezone</Label><Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
              <div><Label>Model override</Label><select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.model_override} onChange={(e) => setForm({ ...form, model_override: e.target.value })}><option value="basic">basic</option><option value="mini">mini</option><option value="low">low</option><option value="flash">flash</option><option value="default">default</option></select></div>
              <div><Label>Delivery mode</Label><select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.delivery_mode} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })}><option value="silent">silent</option><option value="announce">announce</option><option value="announce_on_failure">announce_on_failure</option></select></div>
              <div><Label>Delivery target</Label><Input value={form.delivery_target} onChange={(e) => setForm({ ...form, delivery_target: e.target.value })} placeholder="telegram:chat-id or channel key" /></div>
              <div><Label>Max retries</Label><Input type="number" value={form.max_retries} onChange={(e) => setForm({ ...form, max_retries: Number(e.target.value || 0) })} /></div>
              <div><Label>Retry backoff seconds</Label><Input type="number" value={form.retry_backoff_seconds} onChange={(e) => setForm({ ...form, retry_backoff_seconds: Number(e.target.value || 300) })} /></div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={!!form.announce_on_failure} onChange={(e) => setForm({ ...form, announce_on_failure: e.target.checked })} />Announce on failure</label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />Enabled</label>
              <div className="flex gap-2">
                <Button className="btn-gradient text-white flex-1" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save changes' : 'Create cron job'}</Button>
                {editingId ? <Button variant="outline" onClick={resetForm}>Cancel</Button> : null}
              </div>
            </CardContent>
          </Card>

          <Card className="apollo-card lg:col-span-1">
            <CardHeader><CardTitle>Jobs</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {loading ? <div className="text-muted-foreground">Loading…</div> : null}
              {jobs.map((job) => (
                <button key={job.id} className={`w-full text-left rounded-xl border p-3 ${selectedJobId === job.id ? 'border-primary/60 bg-primary/5' : 'border-border/70'}`} onClick={() => setSelectedJobId(job.id)}>
                  <div className="font-medium">{job.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{job.target_agent} · {job.schedule_kind} · {job.schedule_value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{job.enabled ? 'enabled' : 'paused'} · next {job.next_run_at || 'none'}</div>
                  <div className="text-xs text-muted-foreground mt-1">retries {job.max_retries || 0} · backoff {job.retry_backoff_seconds || 300}s · {job.delivery_mode}</div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handlePauseResume(job); }}>{job.enabled ? 'Pause' : 'Resume'}</Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleRunNow(job); }}>Run now</Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); startEdit(job); }}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(job); }}>Delete</Button>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card className="apollo-card lg:col-span-1">
            <CardHeader><CardTitle>Run history</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!selectedJob ? <div className="text-muted-foreground">Select a job to view runs.</div> : null}
              {selectedJob ? <div className="rounded-xl border border-border/70 p-3 text-xs text-muted-foreground">Selected: <span className="text-foreground font-medium">{selectedJob.name}</span><br />Last status: {selectedJob.last_status || 'none'}<br />Last run: {selectedJob.last_run_at || 'never'}<br />Delivery target: {selectedJob.delivery_target || 'none'}</div> : null}
              {runs.map((run) => (
                <div key={run.id} className="rounded-xl border border-border/70 p-3">
                  <div className="font-medium">{run.status}</div>
                  <div className="text-xs text-muted-foreground mt-1">attempt {run.attempt_number || 1} · scheduled {run.scheduled_for || 'unknown'}</div>
                  {run.output_summary ? <div className="text-xs mt-2">{run.output_summary}</div> : null}
                  {run.error_message ? <div className="text-xs mt-2 text-red-400">{run.error_message}</div> : null}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
